/**
 * Document utility functions for CPF/CNPJ handling.
 * Supports the new alphanumeric CNPJ format (IN 2.229/2024).
 */

/** Remove all non-alphanumeric characters and normalize to uppercase. */
export const unmaskDocument = (doc: string) => doc.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

/** Returns true if the document is a CPF (exactly 11 numeric digits). */
export const isCpf = (doc: string) => /^\d{11}$/.test(unmaskDocument(doc));

/** Applies CPF mask: XXX.XXX.XXX-XX */
export const maskCpf = (cpf: string) => {
    const d = unmaskDocument(cpf);
    if (d.length !== 11) return cpf;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

/** Applies CNPJ mask: XX.XXX.XXX/XXXX-XX (supports alphanumeric). */
export const maskCnpj = (cnpj: string) => {
    const d = unmaskDocument(cnpj);
    if (d.length !== 14) return cnpj;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/** Detects document type and applies the appropriate mask. */
export const maskDocument = (doc: string) => {
    const clean = unmaskDocument(doc);
    if (isCpf(clean)) return maskCpf(clean);
    if (clean.length === 14) return maskCnpj(clean);
    return doc;
};

// ── Validation ──

/** Convert alphanumeric character to its validation value (ASCII - 48). */
const charValue = (char: string) => char.charCodeAt(0) - 48;

/** Calculate a check digit given characters and weights. */
const calculateDigit = (chars: string, weights: number[]): string => {
    const total = weights.reduce((sum, w, i) => sum + charValue(chars[i]) * w, 0);
    const remainder = total % 11;
    return remainder < 2 ? '0' : String(11 - remainder);
};

/** Validates a CPF (11 numeric digits with check digits). */
export const validateCpf = (cpf: string): boolean => {
    const cleaned = unmaskDocument(cpf);
    if (cleaned.length !== 11 || !(/^\d{11}$/.test(cleaned))) return false;
    if (/^(\d)\1+$/.test(cleaned)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cleaned.charAt(i)) * (10 - i);
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleaned.charAt(9))) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cleaned.charAt(i)) * (11 - i);
    digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digit !== parseInt(cleaned.charAt(10))) return false;

    return true;
};

/**
 * Validates a CNPJ (supports alphanumeric format per IN 2.229/2024).
 * First 12 chars: A-Z0-9, last 2: numeric check digits.
 */
export const validateCnpj = (cnpj: string): boolean => {
    const cleaned = unmaskDocument(cnpj);
    if (cleaned.length !== 14) return false;
    if (!/^[A-Z0-9]{12}\d{2}$/.test(cleaned)) return false;
    if (new Set(cleaned.slice(0, 12)).size === 1) return false;

    const weightsFirst = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weightsSecond = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const firstDigit = calculateDigit(cleaned.slice(0, 12), weightsFirst);
    const secondDigit = calculateDigit(cleaned.slice(0, 12) + firstDigit, weightsSecond);

    return cleaned.slice(12) === firstDigit + secondDigit;
};

/** Validates a document (CPF or CNPJ) with check digit verification. */
export const validateDocument = (doc: string): boolean => {
    const cleaned = unmaskDocument(doc);
    if (isCpf(cleaned)) return validateCpf(cleaned);
    if (cleaned.length === 14) return validateCnpj(cleaned);
    return false;
};
